/** Client-only progress state shown while the user stops an assistant response. */

import {InterruptProgressPhase, ProgressData} from 'llm/chat-library/progress/ProgressTypes';

export enum InterruptRequestState {
  NOT_REQUESTED = 'NOT_REQUESTED',
  REQUESTING = 'REQUESTING',
  REQUESTED = 'REQUESTED',
}

export interface InterruptProgressInputs {
  interruptRequestState: InterruptRequestState;
  hasInterruptRequestedEvent: boolean;
  hasInterruptEvent: boolean;
  isThreadActive: boolean;
}

/**
 * Interrupt row phase for the thinking tool list.
 * Stays on "attempting" until the chat thread leaves ACTIVE/QUEUED (tools may still be finishing).
 */
export function getEffectiveInterruptProgressPhase(
  inputs: InterruptProgressInputs
): InterruptProgressPhase | null {
  const isInterruptRequested =
    inputs.interruptRequestState !== InterruptRequestState.NOT_REQUESTED || inputs.hasInterruptRequestedEvent;

  if (!isInterruptRequested) {
    return null;
  }

  if (inputs.hasInterruptEvent || !inputs.isThreadActive) {
    return 'stopped';
  }

  if (inputs.hasInterruptRequestedEvent) {
    return 'attempting';
  }

  if (inputs.interruptRequestState === InterruptRequestState.REQUESTING) {
    return 'requesting';
  }

  return 'attempting';
}

/** True while the agent is still stopping (HTTP in flight or thread still ACTIVE/QUEUED). */
export function isInterruptStopInProgress(inputs: InterruptProgressInputs): boolean {
  const phase = getEffectiveInterruptProgressPhase(inputs);
  return phase === 'requesting' || phase === 'attempting';
}

export function withInterruptProgress(
  progressData: ProgressData | null,
  phase: InterruptProgressPhase
): ProgressData {
  return {
    ...progressData,
    toolCalls: progressData?.toolCalls ?? [],
    interruptProgressPhase: phase,
  };
}
